import { useEffect, useState } from "react";

import { useDebounce } from "shared/hooks";

import { FilterGroupState } from "features/ui/Filters/FilterBuilder/types";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator } from "features/ui/Filters/types";

import { DEFAULT_SEARCH_LIMIT } from "./constants";
import { useSearchConfig } from "./searchConfig";
import { SearchResource } from "./types";

const SEARCH_INPUT_DELAY = 300;

interface Props {
  searchInput: string;
}

const getResourceFilter = (config: SearchResource, searchInput: string) => {
  const filter: FilterGroupState = {
    type: "group",
    id: "filter",
    anyAll: "any",
    children: config.searchFields.map((field) => ({
      type: "row",
      id: field.fieldName,
      attribute: field.fieldName,
      operator: field.filterOperator,
      values: [searchInput],
    })),
  };

  return filter;
};

const getResourceEqualsFilter = (
  config: SearchResource,
  searchInput: string
) => {
  const filter: FilterGroupState = {
    type: "group",
    id: "filter",
    anyAll: "any",
    children: config.searchFields.map(({ fieldName }) => ({
      type: "row",
      id: fieldName,
      attribute: fieldName,
      operator: FilterOperator.IN,
      values: [searchInput],
    })),
  };

  return getFiltersQuery(filter);
};

const getResourceExcludeIdsFilter = (
  config: SearchResource,
  excludedIds: string[]
) => {
  const filter: FilterGroupState = {
    type: "group",
    id: "filter",
    anyAll: "any",
    children: config.searchFields.map(({ fieldName }) => ({
      type: "row",
      id: fieldName,
      attribute: config.idField,
      operator: FilterOperator.NOT_IN,
      values: excludedIds,
    })),
  };

  return filter;
};

export const useGlobalSearch = ({ searchInput }: Props) => {
  const debouncedSearchInput = useDebounce(searchInput, SEARCH_INPUT_DELAY);
  const searchConfig = useSearchConfig();

  const [data, setData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (debouncedSearchInput === "") {
      setData({});
      setLoading(false);

      return;
    }

    // Responses to an older input can still be in flight; drop them.
    let cancelled = false;

    setLoading(true);
    const fetchItems = async (config: SearchResource) => {
      const filter = getResourceFilter(config, debouncedSearchInput);
      const equalsFilter = getResourceEqualsFilter(
        config,
        debouncedSearchInput
      );
      const resultEquals = await config.loader({
        filter: equalsFilter,
        limit: DEFAULT_SEARCH_LIMIT,
        sort: config.orderBy,
      });

      if (resultEquals.data.length >= DEFAULT_SEARCH_LIMIT) {
        return resultEquals.data;
      }

      const resultIds = resultEquals.data.map((x: any) => x[config.idField]);

      const idFilter = getResourceExcludeIdsFilter(config, resultIds);
      const mergedFilter = mergeFilterGroupStates(filter, idFilter);
      const result = await config.loader({
        filter: getFiltersQuery(mergedFilter),
        limit: DEFAULT_SEARCH_LIMIT - resultEquals.data.length,
        sort: config.orderBy,
      });

      return [...resultEquals.data, ...result.data];
    };

    const promises = searchConfig.map(async (config) => {
      const items = await fetchItems(config).catch(() => []);

      if (!cancelled) {
        setData((prev) => ({ ...prev, [config.key]: items }));
      }
    });

    Promise.all(promises).then(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  let firstResultIndex = 0;
  const results = searchConfig.map((config) => {
    const items = data[config.key] ?? [];
    const result = { config, items, firstResultIndex };
    firstResultIndex += items.length;

    return result;
  });

  return {
    results,
    loading,
    debouncedSearchInput,
  };
};
